import { ISlideContentEnumeratorService } from '../../slide-content-enumerator/slide-content-enumerator-service.interface';
import { BaseAnalyzer } from './base-analyzer';

export interface AnalyzerWrapper<IResult> {
    key?: string; // 辅助说明此解析器是干什么用的
    analyzer: BaseAnalyzer<IResult>;
}

export abstract class BaseChecker<
    IAnalyzerWrapper extends AnalyzerWrapper<unknown> = AnalyzerWrapper<unknown>,
    ICheckResult = Record<string, unknown>,
> {
    protected analyzers: IAnalyzerWrapper[]; // 检测器列表

    public constructor(private enumerator: ISlideContentEnumeratorService) {}

    public addAnalyzers(analyzers: IAnalyzerWrapper[]) {
        // 入参填充默认值
        this.analyzers = analyzers.map((analyzer, index) => ({
            ...analyzer,
            key: analyzer.key ?? String(index), // 默认用序号作为标题
        }));
    }

    public analysis() {
        return this.doAnalysis().then(() => this.getResult());
    }

    /**
     * 提供一种默认把结果平铺下来的方法
     * @returns 所有 analyzer 的解析结果
     */
    protected async getDefaultResult(): Promise<Record<string, unknown>> {
        const res: Record<string, unknown> = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const analyzerInfo of this.analyzers) {
            const { key, analyzer } = analyzerInfo;
            res[key ?? 'unknown'] = await analyzer.result;
        }
        return res;
    }

    private async doAnalysis() {
        await this.enumerator.enumerateWithCallback({
            onStartEnumerate: () => {
                this.analyzers.forEach(({ analyzer }) => analyzer.onPresentationStart());
            },
            slideStartCallback: (slide) => {
                this.analyzers.forEach(({ analyzer }) => analyzer.onSlideStart(slide));
            },
            slideEndCallback: (slide) => {
                this.analyzers.forEach(({ analyzer }) => analyzer.onSlideEnd(slide));
            },
            elementCallback: (element) => {
                this.analyzers.forEach(({ analyzer }) => analyzer.onElement(element));
            },
            textParagraphParentBoxCallback: (node) => {
                this.analyzers.forEach(({ analyzer }) => analyzer.onParagraphParentBox(node));
            },
            textElementBoxCallback: (node) => {
                this.analyzers.forEach(({ analyzer }) => analyzer.onTextElementBox(node));
            },
            presentationEndCallback: (presentation) => {
                this.analyzers.forEach(({ analyzer }) => (analyzer.result = analyzer.calculateResult(presentation)));
            },
        });
    }

    /**
     * 自定义组织结果
     */
    protected abstract getResult(): Promise<ICheckResult>;
}
